import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store';
import { fetchFilteredContacts, setFilter } from 'src/features/account/filteredContacts.slice';
import { selectIsFilterActive } from 'src/features/account/filteredContacts.slice';
import { resetPagination, selectLimit } from 'src/features/account/contacts.slice';
import debounce from 'lodash/debounce';
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Collapse,
  Paper,
  Fade,
  Chip,
  Button,
  Divider,
  Tooltip,
  Badge,
  InputBase,
  ClickAwayListener,
  alpha,
  Zoom,
  Alert,
  Snackbar,
  MenuItem,
  Popover,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  InputAdornment,
  Slider,
  SelectChangeEvent,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import TuneIcon from '@mui/icons-material/Tune';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import FilterSelector from './FilterSelector';
import FilterChip from './FilterChip';
import { FilterOption, FilterValue, DateTimeRange } from 'src/components/Contacts/types';

const DEBOUNCE_DELAY = 700; // Increased to 700ms as requested

interface ContactsFilterBarProps {
  isDrawerOpen?: boolean;
  setDrawerOpen?: (open: boolean) => void;
  hideFilterButton?: boolean;
  // New props for the header bar
  viewMode?: 'grid' | 'kanban';
  onViewModeChange?: (mode: 'grid' | 'kanban') => void;
  searchTerm?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch?: () => void;
  onCreateContact?: () => void;
  onImportContacts?: () => void;
  isLoading?: boolean;
  darkMode?: boolean;
}

const ContactsFilterBar: React.FC<ContactsFilterBarProps> = ({
  isDrawerOpen: externalDrawerOpen,
  setDrawerOpen: setExternalDrawerOpen,
  hideFilterButton = false,
  viewMode,
  onViewModeChange,
  searchTerm = '',
  onSearchChange,
  onClearSearch,
  onCreateContact,
  onImportContacts,
  isLoading = false,
  darkMode = false
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Dynamic drawer width based on screen size
  const drawerWidth = isLargeScreen ? 520 : 420;

  // Use a local loading state instead of global one to avoid unnecessary refreshes
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // State for Popover anchor
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // 1) Define new state to track the score range slider value:
  const [scoreRange, setScoreRange] = useState<number[]>([0, 100]);
  const limit = useAppSelector(selectLimit);
  // Add state for hover detection on filter badge
  const [isHovered, setIsHovered] = useState(false);
  
  // State for mobile search expansion
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Use internal drawer state if not controlled externally
  const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);

  // Determine whether to use internal or external drawer state
  const isDrawerOpen = externalDrawerOpen !== undefined ? externalDrawerOpen : internalDrawerOpen;
  const setIsDrawerOpen = setExternalDrawerOpen || setInternalDrawerOpen;

  const initialFilters = {
    first_name: null,
    last_name: null,
    name: null,
    status: null,
    tags: [], // Keep as []
    company_name: null,
    address: null,
    created_at_before: null,
    created_at_after: null,
    last_contacted_at_before: null,
    last_contacted_at_after: null,
    zip: null,
    state: null,
    phone_number: null,
    email: null,
    without_phone_number: false,
    without_email: false,
    company_size: null,
    company_industry: null,
    company_website: null,
    company_address: null,
    stage: null,
    notes: null,
    favorite: null,
    contacted: false,
    score_greater_than: null,
    score_less_than: null,
    limit: 10, // Default limit value
  };
  const isFilterActive = useAppSelector(selectIsFilterActive);
  const filterError = useAppSelector(state => state.filteredContacts.error);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([]);
  const [expandedFilterId, setExpandedFilterId] = useState<string | null>(null);
  const [filterSelectorOpen, setFilterSelectorOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [animateFilters, setAnimateFilters] = useState(false);
  const [miniFilterBarVisible, setMiniFilterBarVisible] = useState(false);
  const [isApplyingFilter, setIsApplyingFilter] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  
  // Function to get active filter count
  const getActiveFilterCount = () => {
    return activeFilters.filter(f => f.value !== null && f.value !== undefined).length;
  };

  // Show error snackbar when filter error occurs
  useEffect(() => {
    if (filterError) {
      console.error('❌ Filter error detected:', filterError);
      setErrorSnackbarOpen(true);
    }
  }, [filterError]);

  // Create a non-debounced version for immediate filter application
  const applyFilters = useCallback((filters: FilterValue[]) => {
    setIsFilterLoading(true);
    const filterParams: Record<string, any> = {};

    filters.forEach(filter => {
      // Skip null values which indicate "any" option
      if (filter.value === null) {
        return;
      }

      // Handle DateTimeRange filters - NEW
      if (filter.option.id.endsWith('_range') && filter.value && typeof filter.value === 'object' && 'mode' in filter.value) {
        const range = filter.value as DateTimeRange;
        const baseField = filter.option.id.replace('_range', '');
        
        if (range.mode === 'before' && range.end) {
          filterParams[`${baseField}_before`] = range.end.toISOString();
        } else if (range.mode === 'after' && range.start) {
          filterParams[`${baseField}_after`] = range.start.toISOString();
        } else if (range.mode === 'between' && range.start && range.end) {
          filterParams[`${baseField}_after`] = range.start.toISOString();
          filterParams[`${baseField}_before`] = range.end.toISOString();
        }
      }
      // Handle special case for email_not_contains to ensure partial matches work
      else if (filter.option.id === 'email_not_contains' && Array.isArray(filter.value)) {
        filterParams[filter.option.id] = filter.value;
      }
      // Handle score range - convert to separate greater/less than parameters
      else if (filter.option.id === 'score' && typeof filter.value === 'object' && filter.value !== null) {
        const scoreRange = filter.value as { min: number; max: number };
        filterParams.score_greater_than = scoreRange.min;
        filterParams.score_less_than = scoreRange.max;
      }
      // Handle Date filters
      else if (filter.value instanceof Date) {
        filterParams[filter.option.id] = filter.value.toISOString();
      }
      // Handle boolean filters (contacted, without_phone_number, without_email, favorite)
      else if (['contacted', 'without_phone_number', 'without_email', 'favorite'].includes(filter.option.id)) {
        filterParams[filter.option.id] = filter.value === true;
      }
      // Handle other array-type filters
      else if (Array.isArray(filter.value)) {
        filterParams[filter.option.id] = filter.value.join(',');
      }
      // Handle non-array filters
      else {
        filterParams[filter.option.id] = filter.value;
      }
    });

    // Update the filter state in Redux
    dispatch(setFilter(filterParams));
  }, [dispatch]);

  // Create the debounced version for typing/frequent updates
  const debouncedFilterFunction = useMemo(() =>
    debounce((filters: FilterValue[]) => {
      applyFilters(filters);
    }, DEBOUNCE_DELAY),
    [applyFilters]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFilterFunction.cancel();
      setIsDrawerOpen(false);
    };
  }, [debouncedFilterFunction]);

  // Monitor the filtered contacts loading state
  const isContactsLoading = useAppSelector(state => state.filteredContacts.contactsLoading);

  // Update loading state based on contacts loading
  useEffect(() => {
    if (!isContactsLoading && isFilterLoading) {
      setIsFilterLoading(false);
    }
  }, [isContactsLoading, isFilterLoading]);

  // Set mini filter bar visible based on active filters
  useEffect(() => {
    console.log('👁️ Updating mini filter bar visibility:',
      activeFilters.length > 0 && !isDrawerOpen ? 'visible' : 'hidden',
      'Active filters:', activeFilters.length,
      'Drawer open:', isDrawerOpen
    );
    setMiniFilterBarVisible(activeFilters.length > 0 && !isDrawerOpen);
  }, [activeFilters.length, isDrawerOpen]);

  const handleFilterSelect = (option: FilterOption | null) => {
    if (!option) return;

    let defaultValue: any;
    switch (option.id) {
      case 'contacted':
        defaultValue = true;
        break;
      case 'email':
      case 'email_not_contains':
      case 'tags':
      case 'tags_not_contains':
      case 'metadata_contains':
      case 'metadata_not_contains':
        defaultValue = [];
        break;
      case 'limit':
        defaultValue = 50;
        break;
      case 'score':
        defaultValue = { min: 0, max: 100 };
        break;
      case 'favorite':
        defaultValue = true;
        break;
      case 'without_phone_number':
      case 'without_email':
        defaultValue = true;
        break;
      default:
        // For datetime range filters, set a default DateTimeRange
        if (option.id.endsWith('_range')) {
          defaultValue = {
            mode: 'after' as const,
            start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
          };
        }
        // For datetime filters, set a default date (30 days ago for 'after' filters, current date for 'before' filters)
        else if (option.id.includes('_after') || option.id.includes('_before') || option.id === 'birthday') {
          const now = new Date();
          if (option.id.includes('_after')) {
            defaultValue = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
          } else {
            defaultValue = now;
          }
        } else {
          defaultValue = '';
        }
    }

    setIsFilterLoading(true);

    setActiveFilters(prev => {
      const newFilters = [...prev, { option, value: defaultValue }];
      debouncedFilterFunction(newFilters);
      return newFilters;
    });

    // Automatically expand the newly added filter
    setExpandedFilterId(option.id);
    setFilterSelectorOpen(false);
  };

  const handleFilterChange = (index: number, newValue: any) => {
    setIsFilterLoading(true);

    setActiveFilters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value: newValue };
      debouncedFilterFunction(updated);
      return updated;
    });
  };

  const handleFilterRemove = (filterId: string) => {

    // Create updated filters array first
    const updatedFilters = activeFilters.filter(filter => filter.option.id !== filterId);

    // Update state
    setActiveFilters(updatedFilters);
    setExpandedFilterId(null);

    // Apply the updated filters immediately without debouncing
    applyFilters(updatedFilters);
  };

  // Function to clear all filters
  const handleClearFilters = () => {
    setIsFilterLoading(true);
    setActiveFilters([]);
    setExpandedFilterId(null);
    dispatch(resetPagination());
    setInputValue('');

    // First dispatch setFilter
    dispatch(setFilter({}));

    // Then fetch contacts and reset loading state when done
    dispatch(fetchFilteredContacts({ limit }))
      .finally(() => {
        setIsFilterLoading(false);
      });
  };

  const handleFilterClick = (filterId: string) => {
    setExpandedFilterId(expandedFilterId === filterId ? null : filterId);
  };

  const handleFilterBlur = (filterId: string) => {
    setActiveFilters(prev => {
      const filterIndex = prev.findIndex(f => f.option.id === filterId);
      if (filterIndex === -1) return prev;

      const filter = prev[filterIndex];
      const isEmpty = Array.isArray(filter.value)
        ? !filter.value.length || filter.value.every(v => !v.trim())
        : typeof filter.value === 'string' && !filter.value.trim();

      if (isEmpty) {
        setIsFilterLoading(true);
        const updated = prev.filter(f => f.option.id !== filterId);
        debouncedFilterFunction(updated);
        setExpandedFilterId(null);
        return updated;
      }
      return prev;
    });
  };

  const handleOpenFilterSelector = () => {
    setFilterSelectorOpen(true);
  };

  const handleCloseFilterSelector = () => {
    setFilterSelectorOpen(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Function to handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape key closes drawer
    if (e.key === 'Escape') {
      setIsDrawerOpen(false);
    }

    // Ctrl+F opens the filter drawer
    if (e.ctrlKey && e.key === 'f') {
      e.preventDefault();
      setIsDrawerOpen(true);
    }
  };

  // Determine the active filter count per group
  const getFilterCountsByGroup = () => {
    const counts: Record<string, number> = {};
    activeFilters.forEach(filter => {
      const group = filter.option.group;
      counts[group] = (counts[group] || 0) + 1;
    });
    return counts;
  };

  const filterCountsByGroup = getFilterCountsByGroup();

  // Get color for filter group
  const getFilterGroupColor = (group: string) => {
    switch (group) {
      case 'Details':
        return theme.palette.primary.main;
      case 'Company':
        return theme.palette.success.main;
      case 'Activity':
        return theme.palette.info.main;
      case 'Advanced':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Active Filters Summary Component
  const ActiveFiltersSummary = () => {
    if (activeFilters.length === 0) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          maxWidth: '100%',
          mt: 1,
          mb: 2
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Active filters:
        </Typography>

        {Object.entries(filterCountsByGroup).map(([group, count]) => (
          <Chip
            key={group}
            size="small"
            label={`${group}: ${count}`}
            sx={{
              backgroundColor: alpha(getFilterGroupColor(group), 0.15),
              color: getFilterGroupColor(group),
              borderRadius: 1,
              height: 24,
              fontWeight: 500,
            }}
          />
        ))}

        {activeFilters.length > 0 && (
          <Tooltip title="Clear all filters">
            <IconButton
              size="small"
              onClick={handleClearFilters}
              sx={{
                p: 0.5,
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <ClearAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Filter group divider component
  const FilterGroupDivider = ({ group, count }: { group: string, count: number }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      mt: 2,
      mb: 1,
      pl: 1
    }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: getFilterGroupColor(group),
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {group}
        {count > 0 && (
          <Chip
            size="small"
            label={count}
            sx={{
              ml: 1,
              height: 20,
              minWidth: 20,
              bgcolor: alpha(getFilterGroupColor(group), 0.15),
              color: getFilterGroupColor(group),
            }}
          />
        )}
      </Typography>
      <Divider sx={{ ml: 1, flex: 1 }} />
    </Box>
  );

  // Main filter selector UI
  const FilterSelectorUI = () => (
    <Box
      sx={{
        position: 'relative',
        mt: 1,
        mb: 2,
      }}
    >
      <Paper
        elevation={0}
        onClick={handleOpenFilterSelector}
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          border: '1px solid',
          borderColor: filterSelectorOpen
            ? `${theme.palette.primary.main}`
            : 'divider',
          borderRadius: 1.5,
          cursor: 'pointer',
          bgcolor: filterSelectorOpen
            ? alpha(theme.palette.primary.main, 0.08)
            : 'background.paper',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
          transition: 'all 0.2s ease',
        }}
      >
        <AddIcon sx={{ ml: 1, mr: 1, color: 'text.secondary' }} />
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontWeight: 400,
          }}
        >
          Add a filter
        </Typography>
        {/* <Box sx={{ ml: 'auto' }}>
          <IconButton
            size="small"
            sx={{ mr: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFilterSelector();
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Box> */}
      </Paper>

      {filterSelectorOpen && (
        <Paper
          elevation={5}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 0.5,
            p: 2,
            zIndex: 1300,
            maxHeight: '60vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Paper
              component="form"
              elevation={0}
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
              }}
            >
              <IconButton sx={{ p: '10px' }} aria-label="search">
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search filters"
                inputProps={{ 'aria-label': 'search filters' }}
                value={searchQuery}
                onChange={handleSearchChange}
                autoFocus
              />
            </Paper>
          </Box>

          <Box sx={{
            overflowY: 'auto',
            maxHeight: 'calc(60vh - 70px)',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.common.black, 0.15),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.common.black, 0.25),
            }
          }}>
            <FilterSelector
              onFilterSelect={handleFilterSelect}
              selectedFilters={activeFilters.map(f => f.option.id)}
              searchQuery={searchQuery}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );

  // Main drawer content
  const drawer = (
    <ClickAwayListener onClickAway={() => filterSelectorOpen && handleCloseFilterSelector()}>
      <Box
        ref={drawerContentRef}
        sx={{
          width: '100%',  // Always full width of its container
          p: 2.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          position: 'relative',
        }}
        onKeyDown={handleKeyDown}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterAltIcon sx={{ mr: 1, color: isFilterActive ? 'primary.main' : 'text.secondary' }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          <IconButton
            onClick={() => setIsDrawerOpen(false)}
            size="small"
            sx={{
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.15),
              },
              width: 32,
              height: 32,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <FilterSelectorUI />

        <ActiveFiltersSummary />

        {activeFilters.length > 0 && (
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.common.black, 0.15),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.common.black, 0.25),
            }
          }}>
            {/* Group and display filters by category */}
            {['Details', 'Company', 'Activity', 'Advanced'].map(group => {
              const groupFilters = activeFilters.filter(f => f.option.group === group);
              if (groupFilters.length === 0) return null;

              return (
                <React.Fragment key={group}>
                  <FilterGroupDivider
                    group={group}
                    count={groupFilters.length}
                  />
                  <Stack spacing={1.5} sx={{ ml: 1 }}>
                    {groupFilters.map((filter, idx) => {
                      const index = activeFilters.findIndex(f => f.option.id === filter.option.id);
                      const isExpanded = expandedFilterId === filter.option.id;

                      return (
                        <Fade key={filter.option.id} in={true} timeout={300}>
                          <Box sx={{
                            position: 'relative',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                            },
                          }}>
                            <Paper
                              elevation={isExpanded ? 2 : 0}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: isExpanded
                                  ? getFilterGroupColor(group)
                                  : alpha(theme.palette.divider, 0.8),
                                bgcolor: isExpanded
                                  ? alpha(getFilterGroupColor(group), 0.07)
                                  : 'transparent',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                  {/* Filter header with label and value summary */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      cursor: 'pointer',
                                      mb: isExpanded ? 1 : 0
                                    }}
                                    onClick={() => handleFilterClick(filter.option.id)}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 500,
                                        color: isExpanded ? getFilterGroupColor(group) : 'text.primary',
                                      }}
                                    >
                                      {filter.option.label}
                                    </Typography>

                                    {!isExpanded && filter.value && (
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{
                                          opacity: 0.8,
                                          maxWidth: 150,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          fontWeight: 400,
                                        }}
                                      >
                                        : {Array.isArray(filter.value)
                                          ? filter.value.join(', ')
                                          : filter.option.id === 'score' && typeof filter.value === 'object'
                                            ? `${(filter.value as any).min}-${(filter.value as any).max}`
                                            : String(filter.value)}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Expanded filter input */}
                                  <Collapse in={isExpanded} timeout={200}>
                                    <Box sx={{ mt: 1 }}>
                                      <FilterChip
                                        filter={filter}
                                        onChange={(newValue) => handleFilterChange(index, newValue)}
                                        onBlur={() => handleFilterBlur(filter.option.id)}
                                        hideDelete
                                        expanded
                                      />
                                    </Box>
                                  </Collapse>
                                </Box>

                                {/* Delete button */}
                                <IconButton
                                  size="small"
                                  onClick={() => handleFilterRemove(filter.option.id)}
                                  sx={{
                                    ml: 0.5,
                                    color: 'text.secondary',
                                    '&:hover': {
                                      color: 'error.main',
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Box>
                        </Fade>
                      );
                    })}
                  </Stack>
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {activeFilters.length === 0 && (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0.7,
            p: 3,
          }}>
            <TuneIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
            <Typography variant="h6" align="center" sx={{ mb: 1, color: 'text.secondary' }}>
              No filters applied
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
              Add filters to narrow down your contacts list
            </Typography>
          </Box>
        )}

        {activeFilters.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={handleClearFilters}
              startIcon={<ClearAllIcon />}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
              }}
            >
              Clear all filters
            </Button>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );

  // Mini filter bar - shown below main toolbar when filters are active
  const MiniFilterBar = () => {
    // Memoize the filtered and sliced active filters to prevent recalculation
    const visibleFilters = useMemo(() => {
      return activeFilters.slice(0, isLargeScreen ? 5 : (isTablet ? 2 : 3));
    }, [activeFilters, isLargeScreen, isTablet]);

    if (!miniFilterBarVisible) return null;

    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1,
        mb: 2,
        flexWrap: 'wrap',
        maxWidth: '100%',
      }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Filters:
        </Typography>

        {visibleFilters.map((filter) => (
          <Chip
            key={filter.option.id}
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {filter.option.label}
                </Typography>
                <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.8 }}>
                  : {Array.isArray(filter.value)
                    ? filter.value.join(', ')
                    : filter.option.id === 'score' && typeof filter.value === 'object'
                      ? `${(filter.value as any).min}-${(filter.value as any).max}`
                      : filter.option.id === 'contacted'
                        ? filter.value === null
                          ? 'Any'
                          : filter.value ? 'Yes' : 'No'
                        : String(filter.value).substring(0, 20)}
                </Typography>
              </Box>
            }
            onDelete={() => handleFilterRemove(filter.option.id)}
            sx={{
              height: 28,
              borderRadius: 1,
              bgcolor: alpha(getFilterGroupColor(filter.option.group), 0.1),
              color: getFilterGroupColor(filter.option.group),
              '& .MuiChip-deleteIcon': {
                color: getFilterGroupColor(filter.option.group),
                '&:hover': {
                  color: theme.palette.error.main,
                },
              },
            }}
          />
        ))}

        {activeFilters.length > (isLargeScreen ? 5 : (isTablet ? 2 : 3)) && (
          <Chip
            size="small"
            label={`+${activeFilters.length - (isLargeScreen ? 5 : (isTablet ? 2 : 3))} more`}
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              height: 28,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              cursor: 'pointer',
            }}
          />
        )}

        <Tooltip title="Clear all filters">
          <IconButton
            size="small"
            onClick={handleClearFilters}
            sx={{
              p: 0.5,
              color: 'text.secondary',
              '&:hover': {
                color: theme.palette.error.main,
              },
            }}
          >
            <ClearAllIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Refresh filters">
          <IconButton
            size="small"
            onClick={handleRefreshFilters}
            sx={{
              p: 0.5,
              color: 'text.secondary',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Edit filters">
          <IconButton
            size="small"
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              p: 0.5,
              ml: 'auto',
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // Handle form submission 
  const handleSubmit = () => {
    // Just reuse the debouncedFilterFunction to avoid code duplication
    debouncedFilterFunction(activeFilters);
  };

  // Function to handle key press events in input fields
  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      // Just use the debounced function directly
      debouncedFilterFunction(activeFilters);
    }
  };

  // Error snackbar to display API error messages
  const renderErrorSnackbar = () => (
    <Snackbar
      open={errorSnackbarOpen}
      autoHideDuration={6000}
      onClose={() => setErrorSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setErrorSnackbarOpen(false)}
        severity="error"
        sx={{ width: '100%' }}
      >
        {filterError && filterError.includes('score is invalid')
          ? 'Score filter format is invalid. Please use a valid range.'
          : filterError || 'An error occurred with your filter'}
      </Alert>
    </Snackbar>
  );

  // Format phone number helper
  const formatPhoneNumber = (phoneNumber: string): string => {
    return phoneNumber.replace(/\D/g, '');
  };

  // Function to close popovers
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Function to refresh filtered contacts
  const handleRefreshFilters = () => {
    if (isFilterActive) {
      setIsFilterLoading(true);

      // Use the active filters by calling the debounced function
      debouncedFilterFunction(activeFilters);
    }
  };

  // Show the header bar if we have the necessary props
  if (viewMode !== undefined && onViewModeChange) {
    return (
      <>
        {/* Condensed Header Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 1, sm: 2 },
            py: { xs: 1.5, sm: 1 },
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: darkMode ? 'background.paper' : 'background.default',
            position: 'relative',
            overflow: 'hidden',
            minHeight: { xs: 64, sm: 56 },
            flexShrink: 0,
          }}
        >
          {/* Search Input - Mobile optimized */}
          <Box sx={{ flexShrink: 0 }}>
            <Tooltip title="Search by name, email, or phone" arrow placement="bottom">
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: { xs: searchExpanded || searchTerm ? 1.5 : 1, sm: 1.5 },
                    py: 0.5,
                    borderRadius: '20px',
                    bgcolor: darkMode ? alpha(theme.palette.grey[700], 0.8) : alpha(theme.palette.grey[200], 0.8),
                    border: `1px solid ${darkMode ? theme.palette.grey[600] : theme.palette.grey[300]}`,
                    minWidth: { xs: searchExpanded || searchTerm ? '140px' : '40px', sm: '250px' },
                    maxWidth: { xs: searchExpanded || searchTerm ? '180px' : '40px', sm: '400px' },
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: darkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                    },
                    '&:focus-within': {
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                  onClick={() => {
                    if (isMobile && !searchExpanded) {
                      setSearchExpanded(true);
                      setTimeout(() => searchInputRef.current?.focus(), 100);
                    }
                  }}
                >
                  <SearchIcon fontSize="small" sx={{ color: darkMode ? 'grey.400' : 'grey.600' }} />
                  <InputBase
                    ref={searchInputRef}
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={onSearchChange}
                    autoComplete="off"
                    onBlur={() => {
                      if (isMobile && !searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    sx={{
                      ml: 0.5,
                      flex: 1,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 400,
                      color: darkMode ? 'grey.100' : 'text.primary',
                      display: { xs: searchExpanded || searchTerm ? 'block' : 'none', sm: 'block' },
                      '& input': {
                        padding: 0,
                      },
                      '& input::placeholder': {
                        opacity: 0.7,
                        color: darkMode ? 'grey.500' : 'text.secondary',
                      }
                    }}
                  />
                  {searchTerm && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearSearch?.();
                      }}
                      sx={{ 
                        p: 0.25,
                        color: darkMode ? 'grey.400' : 'grey.600',
                        '&:hover': {
                          bgcolor: 'transparent',
                          color: darkMode ? 'grey.200' : 'grey.800',
                        }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  {isLoading && (
                    <CircularProgress size={16} sx={{ ml: 0.5, color: theme.palette.primary.main }} />
                  )}
                </Box>
              </Box>
            </Tooltip>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />
          
          {/* View Mode Selector */}
          <Select
            value={viewMode}
            onChange={(e) => onViewModeChange(e.target.value as 'grid' | 'kanban')}
            size="small"
            sx={{
              height: { xs: 32, sm: 36 },
              minWidth: { xs: 100, sm: 120 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              mr: 1,
              '& .MuiSelect-select': {
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              },
            }}
          >
            <MenuItem value="grid">
              <ViewStreamIcon fontSize="small" sx={{ mr: 0.5 }} />
              List View
            </MenuItem>
            <MenuItem value="kanban">
              <ViewAgendaIcon fontSize="small" sx={{ mr: 0.5 }} />
              Kanban View
            </MenuItem>
          </Select>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {/* Filter button */}
            <Tooltip title="Filters" arrow>
              <Badge
                badgeContent={getActiveFilterCount()}
                color="primary"
                invisible={!isFilterActive}
              >
                <IconButton
                  onClick={() => setIsDrawerOpen(true)}
                  size="small"
                  sx={{
                    bgcolor: isFilterActive || isDrawerOpen ?
                      (darkMode ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1)) :
                      'transparent',
                    '&:hover': {
                      bgcolor: darkMode ? alpha(theme.palette.primary.main, 0.25) : alpha(theme.palette.primary.main, 0.2)
                    },
                    color: isFilterActive ? theme.palette.primary.main : 'inherit'
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>

            {/* Add Contact button - icon only on mobile */}
            <Tooltip title="Add Contact" arrow>
              <IconButton
                onClick={onCreateContact}
                size="small"
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  color: theme.palette.primary.main,
                }}
              >
                <PersonAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={onCreateContact}
              size="small"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                boxShadow: 'none',
                backgroundColor: darkMode ? alpha(theme.palette.primary.main, 0.8) : theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                },
              }}
            >
              Add Contact
            </Button>

            {/* Import button */}
            <Tooltip title="Import Contacts" arrow>
              <IconButton 
                onClick={onImportContacts} 
                size="small"
                sx={{ color: 'inherit' }}
              >
                <ImportContactsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Responsive drawer implementation */}
        {isMobile || isTablet ? (
          // Mobile/Tablet: full screen overlay
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            variant="temporary"
            sx={{
              '& .MuiDrawer-paper': {
                width: '100%',
                height: '100%',
                boxShadow: '0 0 20px rgba(0,0,0,0.2)',
              },
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          // Desktop: inline sidebar without blocking
          <Box
            sx={{
              position: 'fixed',
              right: 0,
              top: 0,
              height: '100vh',
              width: isDrawerOpen ? drawerWidth : 0,
              transition: 'width 0.3s ease',
              overflow: 'hidden',
              boxShadow: isDrawerOpen ? '0 0 20px rgba(0,0,0,0.1)' : 'none',
              zIndex: theme.zIndex.drawer,
              bgcolor: 'background.paper',
              borderLeft: isDrawerOpen ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            {isDrawerOpen && drawer}
          </Box>
        )}
      </>
    );
  }

  // Original filter bar functionality when not showing header
  return (
    <>
      {!hideFilterButton && (
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Badge
            badgeContent={activeFilters.length}
            color="primary"
            invisible={activeFilters.length === 0}
            overlap="circular"
          >
            <IconButton
              ref={filterButtonRef}
              onClick={() => setIsDrawerOpen(true)}
              color={isFilterActive ? "primary" : "default"}
              sx={{
                position: 'relative',
                bgcolor: isFilterActive ? alpha(theme.palette.primary.main, 0.1) : undefined,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: isFilterActive
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.action.hover, 0.8),
                },
              }}
            >
              {/* {isFilterLoading ? (
                <CircularProgress size={24} />
              ) : (
                <FilterListIcon />
              )} */}
            </IconButton>
          </Badge>

          {isFilterActive && isHovered && !isFilterLoading && (
            <Tooltip title="Refresh filters">
              <IconButton
                onClick={handleRefreshFilters}
                size="small"
                sx={{
                  opacity: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 1,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {!hideFilterButton && <MiniFilterBar />}
      {renderErrorSnackbar()}

      {/* Responsive drawer implementation */}
      {isMobile || isTablet ? (
        // Mobile/Tablet: full screen overlay
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          variant="temporary"
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              height: '100%',
              boxShadow: '0 0 20px rgba(0,0,0,0.2)',
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        // Desktop: inline sidebar without blocking
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: 0,
            height: '100vh',
            width: isDrawerOpen ? drawerWidth : 0,
            transition: 'width 0.3s ease',
            overflow: 'hidden',
            boxShadow: isDrawerOpen ? '0 0 20px rgba(0,0,0,0.1)' : 'none',
            zIndex: theme.zIndex.drawer,
            bgcolor: 'background.paper',
            borderLeft: isDrawerOpen ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          {isDrawerOpen && drawer}
        </Box>
      )}
    </>
  );
};

export default ContactsFilterBar;
